import { useState, type FormEvent } from 'react'

import { Alert, Button, Card, CardHeader, SelectField } from '@/components/layout/ui'
import { useSession } from '@/lib/session'
import {
  readVoiceEngine,
  readVoicePreference,
  saveVoiceEngine,
  saveVoicePreference,
  VOICE_LANGUAGES,
  type VoiceEngine,
  type VoicePreference,
} from '@/lib/voicePreferences'

export default function VoiceSettingsPage() {
  const { user, voiceTranscription } = useSession()
  return user ? <VoicePreferencesForm key={user.id} userId={user.id} serverAvailable={voiceTranscription} /> : null
}

function VoicePreferencesForm({ userId, serverAvailable }: { userId: number; serverAvailable: boolean }) {
  const [preference, setPreference] = useState(() => readVoicePreference(userId))
  const [engine, setEngine] = useState<VoiceEngine>(() => readVoiceEngine(userId))
  const [saved, setSaved] = useState({ preference, engine })
  const [feedback, setFeedback] = useState<'saved' | 'error' | null>(null)
  const language = VOICE_LANGUAGES.find(({ dialects }) => dialects.some(({ locale }) => locale === preference))
  const dirty = preference !== saved.preference || engine !== saved.engine

  function choose(value: VoicePreference) {
    setPreference(value)
    setFeedback(null)
  }

  function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (saveVoicePreference(userId, preference) && saveVoiceEngine(userId, engine)) {
      setSaved({ preference, engine })
      setFeedback('saved')
    } else {
      setFeedback('error')
    }
  }

  return (
    <Card>
      <CardHeader title="Voice input" description="Choose how the microphone works and the language and dialect you speak when adding tasks or drafting with AI." />
      <form onSubmit={save} className="space-y-5 p-5">
        <SelectField
          label="Transcription engine"
          value={engine}
          onChange={(event) => {
            setEngine(event.target.value === 'browser' ? 'browser' : 'whisper')
            setFeedback(null)
          }}
          hint={
            serverAvailable
              ? engine === 'whisper'
                ? 'Records up to 90 seconds, then transcribes on the Taskly server with the Whisper model. Works in every browser and handles Arabic dialects well. Audio is discarded after transcription.'
                : 'Uses your browser’s own live speech recognition. Words appear as you speak; availability and quality depend on the browser.'
              : 'Server transcription is not set up on this deployment, so the browser engine is used.'
          }
        >
          <option value="whisper">Whisper on the server (recommended)</option>
          <option value="browser">Browser live dictation</option>
        </SelectField>
        <div className="grid gap-5 sm:grid-cols-2">
          <SelectField
            label="Language"
            value={language?.code ?? 'browser'}
            onChange={(event) => choose(VOICE_LANGUAGES.find(({ code }) => code === event.target.value)?.dialects[0].locale ?? 'browser')}
            hint={preference === 'browser' ? `Uses your browser language (${navigator.language || 'en-US'}).` : undefined}
          >
            <option value="browser">Browser default</option>
            {VOICE_LANGUAGES.map(({ code, label }) => <option key={code} value={code}>{label}</option>)}
          </SelectField>
          {language && (
            <SelectField
              label="Dialect — اللهجة"
              value={preference}
              onChange={(event) => {
                const dialect = language.dialects.find(({ locale }) => locale === event.target.value)
                if (dialect) choose(dialect.locale)
              }}
              hint="Choose the dialect closest to how you speak."
            >
              {language.dialects.map(({ locale, label }) => <option key={locale} value={locale}>{label}</option>)}
            </SelectField>
          )}
        </div>
        <p className="text-sm text-ink-500 dark:text-ink-400">
          Applies to task titles, descriptions, AI briefs, and draft edits. Saved for your account in this browser.
        </p>
        {feedback === 'saved' && <Alert tone="success">Voice settings saved.</Alert>}
        {feedback === 'error' && <Alert tone="danger">Your browser could not save this preference. Allow site storage and try again.</Alert>}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={!dirty}>Save voice settings</Button>
        </div>
      </form>
    </Card>
  )
}
